import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import LocationPrompt from "./LocationPrompt";
import { IconButton, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import {
  addToCart,
  removeFromCart,
} from "../redux/retailerWholesalerCartSlice";
import { ReactComponent as EmptyCartSVG } from "../assets/img/Emptycart.svg";
import { formatCurrency } from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { StruckPrice, VendorOfferLine } from "./PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import {
  linesSubtotal,
  cartMinimumVerdict,
  minimumOrderMessage,
} from "../utils/minimumOrder";
import MinimumOrderNotice from "./MinimumOrderNotice";
import useCoverage from "../redux/useCoverage";
import CoverageBanner from "./CoverageBanner";
import "./RetailerWholesalerCart.css";
import CartSummary from "./CartSummary";
import useDeliveryEstimate from "../hooks/useDeliveryEstimate";
import { CartPromiseNote } from "./LusukuPromise";

const RetailerWholesalerCart = ({ onClose }) => {
  const dispatch = useDispatch();
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const history = useHistory();
  const currentVendor = useSelector(
    (state) => state.retailerWholesalerCart.currentVendor
  );
  const cart = useSelector(
    (state) => state.retailerWholesalerCart.carts[currentVendor] || {}
  );
  const cartItems = Object.values(cart);

  /* THIS vendor's goods, and only this vendor's. `retailerWholesalerCart.total`
     is the sum of EVERY vendor's cart at once (see the slice's
     calculateTotal), so it both overstated the figure shown here and would
     wave a short basket through the minimum-order gate whenever a second
     vendor's cart happened to be sitting beside it. Each vendor ships as its
     own order with its own rider, so the number has to be per vendor. */
  const goodsSubtotal = linesSubtotal(cartItems);

  // Minimum order volume (wholesale listings). The server refuses a basket
  // under the vendor's minimum at order-create; catching it here means the
  // customer fixes it in the cart instead of at the payment step.
  const minimumOf = (item) => Math.max(1, Number(item.minimumOrderVolume) || 1);
  const shortfalls = cartItems
    .map((item) => ({ item, missing: minimumOf(item) - Number(item.quantity || 0) }))
    .filter(({ missing }) => missing > 0);
  const firstShortfall = shortfalls[0] || null;

  // Minimum order VALUE: the same rule one level up, on the money rather than
  // the units. Goods only, so the delivery fee never helps a basket clear it.
  const minimumOrderValue = useMinimumOrderValue();
  /* The cart as the server prices it (POST /api/quote): the delivery fee, the
     total and the minimum-order verdict all come from this one answer, and the
     cart's own sum stands in only until it arrives. */
  const estimate = useDeliveryEstimate({
    vendorId: currentVendor,
    items: cartItems,
    enabled: cartItems.length > 0,
  });
  const minimum = cartMinimumVerdict(estimate, { subtotal: goodsSubtotal, minimum: minimumOrderValue });
  const minimumShortfall = minimum.shortfall;

  // Pre-checkout coverage gate (backend /api/coverage — same decision as the
  // order-create gate) so the customer can't proceed to an undeliverable order.
  const coverage = useCoverage({ vendorId: currentVendor, vendorType: "retailer" });
  const canProceed =
    cartItems.length > 0 &&
    shortfalls.length === 0 &&
    minimumShortfall === 0 &&
    coverage.hasLocation &&
    coverage.isConfirmed;

  const handleIncrease = (product) => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: 1 }));
  };

  const handleFillToMinimum = (product, missing) => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: missing }));
  };

  const handleDecrease = (product) => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: -1 }));
    if (product.quantity === 1) {
      dispatch(
        removeFromCart({ vendorId: currentVendor, productId: product._id })
      );
    }
  };

  const checkoutHandler = () => {
    if (!canProceed) return;
    history.push("/retailer-wholesaler-shipping");
  };

  return (
    <>
    <div className="retailer-cart-overlay">
      <div className="retailer-cart-modal">
        {/* Header */}
        <div className="retailer-cart-header">
          <IconButton className="retailer-cart-close-btn" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <h2 className="retailer-cart-title">Your Order</h2>
        </div>

        {/* Content */}
        <div className="retailer-cart-content">
          {cartItems.length === 0 ? (
            <div className="retailer-empty-cart">
              <div className="retailer-empty-cart-icon">
                <EmptyCartSVG />
              </div>
              <p className="retailer-empty-cart-text">
                You haven't added any products yet. When you do, you'll see them
                here!
              </p>
            </div>
          ) : (
            <div className="retailer-cart-items">
              {cartItems.map((item) => (
                <div key={item._id} className="retailer-cart-item">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="retailer-cart-item-image"
                  />

                  <div className="retailer-cart-item-details">
                    <h4 className="retailer-cart-item-name">{item.name}</h4>
                    <p className="retailer-cart-item-price">
                      Total:{" "}
                      {lineHasDeal(item) && (
                        <StruckPrice>
                          {formatCurrency(item.listPrice * item.quantity)}
                        </StruckPrice>
                      )}
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                    {minimumOf(item) > 1 && (
                      <p
                        className="retailer-cart-item-min"
                        style={{
                          margin: "2px 0 0",
                          fontSize: 12,
                          color: item.quantity < minimumOf(item) ? "#b23b00" : "#6b7280",
                        }}
                      >
                        Minimum order {minimumOf(item)} units
                        {item.quantity < minimumOf(item) && (
                          <>
                            {" "}
                            <button
                              type="button"
                              onClick={() => handleFillToMinimum(item, minimumOf(item) - item.quantity)}
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                color: "#0f4c45",
                                fontWeight: 700,
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              add {minimumOf(item) - item.quantity} more
                            </button>
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="retailer-cart-item-controls">
                    <IconButton
                      className="retailer-quantity-btn retailer-quantity-btn-minus"
                      onClick={() => handleDecrease(item)}
                      size="small"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>

                    <span className="retailer-quantity-display">
                      {item.quantity}
                    </span>

                    <IconButton
                      className="retailer-quantity-btn retailer-quantity-btn-plus"
                      onClick={() => handleIncrease(item)}
                      size="small"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="retailer-cart-footer">
            <CoverageBanner
              variant="inline"
              deliverable={coverage.deliverable}
              reason={coverage.reason}
              distanceKm={coverage.distanceKm}
              radius={coverage.radius}
              hasLocation={coverage.hasLocation}
              status={coverage.status}
              error={coverage.error}
              onRetry={coverage.refresh}
              onChangeLocation={() => setShowLocationPrompt(true)}
            />
            <MinimumOrderNotice
              shortfall={minimum.shortfall}
              minimum={minimum.minimum}
              subtotal={minimum.subtotal}
              vendorType="shop"
              style={{ margin: "6px 16px" }}
            />
            <VendorOfferLine
              amount={cartOfferSavings(cartItems)}
              format={formatCurrency}
              style={{ padding: "0 16px 10px" }}
            />
            <div style={{ padding: "0 16px" }}>
              <CartSummary
                estimate={estimate}
                subtotal={goodsSubtotal}
                vendorId={currentVendor}
                vendorType="shop"
              />
              <CartPromiseNote />
            </div>

            <Button
              variant="contained"
              className="retailer-checkout-btn"
              onClick={checkoutHandler}
              disabled={!canProceed}
            >
              {firstShortfall
                ? `Add ${firstShortfall.missing} more ${firstShortfall.item.name} (min ${minimumOf(firstShortfall.item)})`
                : minimumShortfall > 0
                ? minimumOrderMessage(minimumShortfall, formatCurrency)
                : coverage.isFailed
                ? "Delivery check failed — retry above"
                : coverage.isOutOfRange
                  ? "Vendor doesn't deliver here"
                  : !coverage.hasLocation
                    ? "Set your delivery location"
                    : !coverage.isConfirmed
                      ? "Checking delivery coverage…"
                      : "Proceed to Checkout"}
            </Button>
          </div>
        )}
      </div>
    </div>
    {showLocationPrompt && (
      <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
    )}
    </>
  );
};

export default RetailerWholesalerCart;
